import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Music, User, AtSign, Link2, FileText, CheckCircle } from 'lucide-react';
import { api } from '../api';

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function DemoSubmission() {
  const [form, setForm] = useState({ name: '', email: '', spotify_link: '', soundcloud_link: '', bio: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      await api.submitContact({
        name: form.name,
        email: form.email,
        message: `[DEMO SUBMISSION]\n\nArtist: ${form.name}\nEmail: ${form.email}\nSpotify: ${form.spotify_link || 'N/A'}\nSoundCloud: ${form.soundcloud_link || 'N/A'}\n\nBio: ${form.bio}\n\nMessage: ${form.message}`,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: '01', title: 'Prepare Your Music', desc: 'Have your best tracks ready on Spotify, SoundCloud, or a private link.' },
    { num: '02', title: 'Fill the Form Below', desc: 'Tell us about yourself and your music. Be authentic — we value originality.' },
    { num: '03', title: 'Wait for Review', desc: 'Our A&R team reviews every submission within 14 business days.' },
  ];

  return (
    <div className="demo-page">
      <div className="demo-hero">
        <motion.div className="demo-hero-badge"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        ><Music size={16} /> Open for Submissions</motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >Submit Your <span>Demo</span></motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >Think your sound belongs on CloudfiMusic? We're always looking for fresh lofi, ambient, and chill artists to join our roster.</motion.p>
      </div>

      {/* Steps */}
      <div className="demo-steps">
        {steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="demo-step">
              <span className="demo-step-num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Form */}
      {success ? (
        <motion.div className="demo-success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <CheckCircle size={48} />
          <h2>Demo Received!</h2>
          <p>Thank you for your submission. Our team will review it and get back to you within 14 business days.</p>
        </motion.div>
      ) : (
        <Reveal>
          <div className="demo-form-card">
            <form className="demo-form" onSubmit={handleSubmit}>
              {error && <div className="ct-error">{error}</div>}
              <div className="ct-row">
                <div className="ct-field">
                  <label>Artist / Producer Name *</label>
                  <div className="ct-input-wrap">
                    <User size={16} className="ct-input-icon" />
                    <input name="name" value={form.name} onChange={handleChange} placeholder="Your artist name" />
                  </div>
                </div>
                <div className="ct-field">
                  <label>Email *</label>
                  <div className="ct-input-wrap">
                    <AtSign size={16} className="ct-input-icon" />
                    <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" />
                  </div>
                </div>
              </div>
              <div className="ct-row">
                <div className="ct-field">
                  <label>Spotify Link</label>
                  <div className="ct-input-wrap">
                    <Link2 size={16} className="ct-input-icon" />
                    <input name="spotify_link" value={form.spotify_link} onChange={handleChange} placeholder="https://open.spotify.com/artist/..." />
                  </div>
                </div>
                <div className="ct-field">
                  <label>SoundCloud Link</label>
                  <div className="ct-input-wrap">
                    <Link2 size={16} className="ct-input-icon" />
                    <input name="soundcloud_link" value={form.soundcloud_link} onChange={handleChange} placeholder="https://soundcloud.com/..." />
                  </div>
                </div>
              </div>
              <div className="ct-field">
                <label>Short Bio</label>
                <div className="ct-input-wrap ct-textarea-wrap">
                  <FileText size={16} className="ct-input-icon ct-textarea-icon" />
                  <textarea name="bio" value={form.bio} onChange={handleChange} placeholder="Tell us about yourself and your musical journey..." rows={3} />
                </div>
              </div>
              <div className="ct-field">
                <label>Message / Why CloudfiMusic? *</label>
                <div className="ct-input-wrap ct-textarea-wrap">
                  <FileText size={16} className="ct-input-icon ct-textarea-icon" />
                  <textarea name="message" value={form.message} onChange={handleChange} placeholder="Why do you think your music fits CloudfiMusic?" rows={4} />
                </div>
              </div>
              <button type="submit" className="ct-submit" disabled={loading}>
                <span>{loading ? 'Submitting...' : 'Submit Demo'}</span>
                <Send size={16} />
              </button>
            </form>
          </div>
        </Reveal>
      )}
    </div>
  );
}
